import { useForm } from 'react-hook-form'
import { useEffect } from 'react'
import { X, User, Mail, Phone, MapPin, FileText, UserCircle } from 'lucide-react'
import { Button } from '../common'

function CustomerForm({ 
  isOpen, 
  editingCustomer, 
  onClose, 
  onSubmit, 
  isLoading 
}) {
  const { register, handleSubmit, reset, formState: { errors } } = useForm()

  // Reset form when editing customer changes
  useEffect(() => {
    if (editingCustomer) {
      reset({
        id: editingCustomer.id,
        firstName: editingCustomer.firstName || '',
        lastName: editingCustomer.lastName || '',
        email: editingCustomer.email || '',
        phone: editingCustomer.phone || '',
        address: editingCustomer.address || '',
        city: editingCustomer.city || '',
        state: editingCustomer.state || '',
        pincode: editingCustomer.pincode || '',
        customerType: editingCustomer.customerType || 'Regular',
        notes: editingCustomer.notes || '',
        isActive: editingCustomer.isActive !== undefined ? editingCustomer.isActive : true
      })
    } else {
      reset({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        pincode: '',
        customerType: 'Regular',
        notes: '',
        isActive: true
      })
    }
  }, [editingCustomer, reset])

  const handleSubmitForm = (data) => {
    // Remove id field from payload (id is used in URL for updates, not in payload)
    const submitData = { ...data }
    delete submitData.id
    
    console.log('CustomerForm - Submitting data:', submitData)
    console.log('CustomerForm - Editing customer:', editingCustomer)
    onSubmit(submitData)
  }

  const handleClose = () => {
    reset({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      pincode: '',
      customerType: 'Regular',
      notes: '',
      isActive: true
    })
    onClose()
  }

  return (
    <>
      {/* Overlay with fade animation */}
      <div 
        className={`fixed inset-0 bg-secondary-900 bg-opacity-50 z-40 transition-all duration-300 ease-out ${
          isOpen 
            ? 'opacity-100 pointer-events-auto' 
            : 'opacity-0 pointer-events-none'
        }`}
        onClick={handleClose}
        style={{
          transition: 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      />

      {/* Sidebar with slide and scale animation */}
      <div 
        className={`fixed right-0 top-0 h-full w-full max-w-2xl bg-white shadow-2xl z-50 flex flex-col transform transition-all duration-300 ease-out ${
          isOpen 
            ? 'translate-x-0 opacity-100' 
            : 'translate-x-full opacity-0'
        }`}
        style={{
          transition: 'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'transform, opacity'
        }}
      >
        {/* Header with fade-in animation */}
        <div 
          className={`flex-shrink-0 border-b border-secondary-200 bg-white px-6 py-5 transition-all duration-300 ${
            isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
          }`}
          style={{
            transitionDelay: isOpen ? '0.1s' : '0s'
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={`flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-full bg-primary-100 transition-all duration-300 ${
                isOpen ? 'scale-100 rotate-0' : 'scale-0 rotate-180'
              }`}
              style={{
                transitionDelay: isOpen ? '0.15s' : '0s'
              }}>
                <User className="h-5 w-5 text-primary-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-secondary-900">
                  {editingCustomer ? 'Edit Customer' : 'Add New Customer'}
                </h3>
                <p className="text-sm text-secondary-500 mt-0.5">
                  {editingCustomer ? 'Update customer information' : 'Enter customer details'}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="flex-shrink-0 rounded-md p-2 text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100 transition-all duration-200 hover:scale-110 active:scale-95"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Scrollable Content with staggered animation */}
        <div 
          className={`flex-1 overflow-y-auto px-6 py-6 transition-all duration-300 ${
            isOpen ? 'opacity-100' : 'opacity-0'
          }`}
          style={{
            transitionDelay: isOpen ? '0.2s' : '0s'
          }}
        >
            <form onSubmit={handleSubmit(handleSubmitForm)} className="space-y-6">
              {/* Hidden ID field for updates */}
              <input type="hidden" {...register('id')} />
              
              {/* Personal Information */}
              <div 
                className={`space-y-4 transition-all duration-300 ${
                  isOpen ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'
                }`}
                style={{
                  transitionDelay: isOpen ? '0.25s' : '0s'
                }}
              >
                <h4 className="text-base font-medium text-secondary-900 border-b border-secondary-200 pb-2 flex items-center">
                  <UserCircle className="h-4 w-4 mr-2" />
                  Personal Information
                </h4>
                
                <div className="bg-secondary-50 rounded-lg p-4 space-y-4">
                  <div>
                    <label className="block text-base font-medium text-secondary-700 mb-1">
                      First Name *
                    </label>
                    <input
                      {...register('firstName', { required: 'First name is required' })}
                      className="input"
                      placeholder="Enter first name"
                    />
                    {errors.firstName && (
                      <p className="text-danger-600 text-base mt-1">{errors.firstName.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-base font-medium text-secondary-700 mb-1">
                      Last Name *
                    </label>
                    <input
                      {...register('lastName', { required: 'Last name is required' })}
                      className="input"
                      placeholder="Enter last name"
                    />
                    {errors.lastName && (
                      <p className="text-danger-600 text-base mt-1">{errors.lastName.message}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div 
                className={`space-y-4 transition-all duration-300 ${
                  isOpen ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'
                }`}
                style={{
                  transitionDelay: isOpen ? '0.35s' : '0s'
                }}
              >
                <h4 className="text-base font-medium text-secondary-900 border-b border-secondary-200 pb-2 flex items-center">
                  <Mail className="h-4 w-4 mr-2" />
                  Contact Information
                </h4>
                
                <div className="bg-secondary-50 rounded-lg p-4 space-y-4">
                  <div>
                    <label className="flex items-center text-base font-medium text-secondary-700 mb-1">
                      <Mail className="h-4 w-4 mr-1 text-secondary-500" />
                      Email
                    </label>
                    <input
                      {...register('email', {
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Invalid email address'
                        }
                      })}
                      type="email"
                      className="input"
                      placeholder="customer@example.com"
                    />
                    {errors.email && (
                      <p className="text-danger-600 text-base mt-1">{errors.email.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="flex items-center text-base font-medium text-secondary-700 mb-1">
                      <Phone className="h-4 w-4 mr-1 text-secondary-500" />
                      Phone *
                    </label>
                    <input
                      {...register('phone', { required: 'Phone number is required' })}
                      type="tel"
                      className="input"
                      placeholder="+1 234 567 8900"
                    />
                    {errors.phone && (
                      <p className="text-danger-600 text-base mt-1">{errors.phone.message}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Address Information */}
              <div 
                className={`space-y-4 transition-all duration-300 ${
                  isOpen ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'
                }`}
                style={{
                  transitionDelay: isOpen ? '0.45s' : '0s'
                }}
              >
                <h4 className="text-base font-medium text-secondary-900 border-b border-secondary-200 pb-2 flex items-center">
                  <MapPin className="h-4 w-4 mr-2" />
                  Address Information
                </h4>
                
                <div className="bg-secondary-50 rounded-lg p-4 space-y-4">
                  <div>
                    <label className="block text-base font-medium text-secondary-700 mb-1">
                      Street Address
                    </label>
                    <textarea
                      {...register('address')}
                      className="input"
                      rows={3}
                      placeholder="Enter full street address"
                    />
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-base font-medium text-secondary-700 mb-1">
                        City
                      </label>
                      <input
                        {...register('city')}
                        className="input"
                        placeholder="Enter city"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-base font-medium text-secondary-700 mb-1">
                          State
                        </label>
                        <input
                          {...register('state')}
                          className="input"
                          placeholder="Enter state"
                        />
                      </div>
                      <div>
                        <label className="block text-base font-medium text-secondary-700 mb-1">
                          Pincode
                        </label>
                        <input
                          {...register('pincode')}
                          className="input"
                          placeholder="Enter pincode"
                          maxLength={10}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Customer Details */}
              <div 
                className={`space-y-4 transition-all duration-300 ${
                  isOpen ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'
                }`}
                style={{
                  transitionDelay: isOpen ? '0.55s' : '0s'
                }}
              >
                <h4 className="text-base font-medium text-secondary-900 border-b border-secondary-200 pb-2 flex items-center">
                  <FileText className="h-4 w-4 mr-2" />
                  Customer Details
                </h4>
                
                <div className="bg-secondary-50 rounded-lg p-4 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-base font-medium text-secondary-700 mb-1">
                        Customer Type
                      </label>
                      <select {...register('customerType')} className="input">
                        <option value="Regular">Regular</option>
                        <option value="VIP">VIP</option>
                        <option value="Walk-in">Walk-in</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-base font-medium text-secondary-700 mb-1">
                        Status
                      </label>
                      <select {...register('isActive')} className="input">
                        <option value={true}>Active</option>
                        <option value={false}>Inactive</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-base font-medium text-secondary-700 mb-1">
                      Additional Notes
                    </label>
                    <textarea
                      {...register('notes')}
                      className="input"
                      rows={3}
                      placeholder="Enter any additional notes or special instructions about the customer"
                    />
                  </div>
                </div>
              </div>

              {/* Footer with buttons */}
              <div 
                className={`flex justify-end space-x-3 pt-6 border-t border-secondary-200 mt-6 transition-all duration-300 ${
                  isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
                style={{
                  transitionDelay: isOpen ? '0.65s' : '0s'
                }}
              >
                <Button
                  type="button"
                  onClick={handleClose}
                  variant="outline"
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  loading={isLoading}
                  disabled={isLoading}
                >
                  {isLoading ? 'Saving...' : editingCustomer ? 'Update Customer' : 'Create Customer'}
                </Button>
              </div>
            </form>
        </div>
      </div>
      
      {/* Prevent body scroll when sidebar is open */}
      {isOpen && (
        <style>{`
          body {
            overflow: hidden;
          }
        `}</style>
      )}
    </>
  )
}

export default CustomerForm
